import { format } from 'date-fns'
import { Trash2, CalendarDays, Flag } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { CalendarHeaderRead } from '@/types'

interface CalendarCardProps {
  calendar: CalendarHeaderRead
  onClick: () => void
  onDelete: () => void
}

export function CalendarCard({
  calendar,
  onClick,
  onDelete,
}: CalendarCardProps) {
  const formattedLastEdit = calendar.updated_at
    ? format(new Date(calendar.updated_at), 'MMM d, yyyy')
    : 'N/A'

  return (
    <Card
      className="cursor-pointer transition-shadow hover:shadow-md"
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base font-semibold truncate">
            {calendar.name}
          </CardTitle>
          {calendar.description && (
            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
              {calendar.description}
            </p>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 cursor-pointer text-muted-foreground hover:text-destructive"
          onClick={e => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-3 pb-4">
        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="gap-1">
            <CalendarDays className="h-3 w-3" />
            {calendar.event_count ?? 0}{' '}
            {calendar.event_count === 1 ? 'event' : 'events'}
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <Flag className="h-3 w-3" />
            {calendar.milestone_count ?? 0}{' '}
            {calendar.milestone_count === 1 ? 'milestone' : 'milestones'}
          </Badge>
          {!calendar.is_active && (
            <Badge variant="outline" className="text-muted-foreground">
              Inactive
            </Badge>
          )}
        </div>

        {/* Where in use & last edit */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {(calendar.plan_count ?? 0) > 0 ? (
            <span>
              Used by {calendar.plan_count}{' '}
              {calendar.plan_count === 1 ? 'plan' : 'plans'}
            </span>
          ) : (
            <span>Not in use</span>
          )}
          <span>Last edit: {formattedLastEdit}</span>
        </div>
      </CardContent>
    </Card>
  )
}
